import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory, useParams } from 'react-router-dom'

import { useSnackbar } from '../../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../../components/stories/molecules/snackbar/constants'

import MoveItemsModal from '../../../../components/move-items-modal'
import { useGetProjectDetailsQuery } from '../../../../react-query/project/query/use-get-project-details-query'
import {
  excludeOptions,
  useGetProjectFolderDetailsQuery
} from '../../../../react-query/project/query/use-get-project-folder-details-query'
import { projectMenuApiErrorCodeOptions } from '../../constants'
import { useMoveProjectsAndFoldersMutation } from '../../../../react-query/project/mutation/use-move-projects-and-folders-mutation'
import { getOrganizationName } from '../../../../react-query/authentication/query/use-me-query'
import { useProjectMenuListViewControl } from '../../../../hooks/use-project-menu-list-view-control'

const MoveProjectModal = () => {
  const history = useHistory()
  const { formatMessage } = useIntl()
  const { addSnackbar } = useSnackbar()

  const { viewType, tab } = useProjectMenuListViewControl()

  const companyName = getOrganizationName()

  const { projectId } = useParams()
  const [moveItemsError, setMoveItemsError] = useState(null)
  const [destinationFolderHint, setDestinationFolderHint] = useState(null)

  const showSnackbarSuccess = () => {
    addSnackbar({
      message: formatMessage({ id: 'moveItems_project_moved' }),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  // Queries
  const { data: projectDetails, isLoading: projectDetailsLoading } =
    useGetProjectDetailsQuery({ projectId })

  const { data: folderDetails, isLoading: folderDetailsLoading } =
    useGetProjectFolderDetailsQuery(
      projectDetails?.parentId,
      [excludeOptions.PROJECTS],
      {
        enabled: !!projectDetails?.parentId
      }
    )

  // Mutations
  const { mutate: moveItems, isPending: moveItemsLoading } =
    useMoveProjectsAndFoldersMutation({
      onSuccess: handleMoveProjectSuccess,
      onError: handleMoveProjectError
    })

  // handlers
  const handleOnClose = () => {
    history.push(`/project?view=${viewType}&tab=${tab}`)
  }

  function handleMoveProjectError(error) {
    // user doesn't have permission to move items
    if (
      error &&
      error?.response?.data?.status ===
        projectMenuApiErrorCodeOptions.NO_PERMISSION_TO_CREATE_PROJECT_OR_FOLDER
    ) {
      return setMoveItemsError(
        formatMessage({
          id: 'projectMenu_no_permission_to_move'
        })
      )
    }

    if (
      error &&
      error?.response?.data?.status ===
        projectMenuApiErrorCodeOptions.FOLDER_NO_LOGER_EXISTS
    ) {
      return setDestinationFolderHint(
        formatMessage({
          id: 'recursiveFolderList_the_selected_folder_no_longer_exists'
        })
      )
    }

    return setMoveItemsError(
      formatMessage({
        id: 'moveItems_move_error'
      })
    )
  }

  function handleMoveProjectSuccess() {
    handleOnClose()
    showSnackbarSuccess()
  }

  const handleMoveItems = ({ parentFolderId }) => {
    setMoveItemsError(null)
    moveItems({
      projectIds: [projectId],
      destinationFolderId: parentFolderId
    })
  }

  const isLoading = projectDetailsLoading || folderDetailsLoading
  const modalTitle = formatMessage({ id: 'projects_moveProject' })

  const getOldFolderName = () => {
    if (folderDetails?.name === companyName || !folderDetails?.name) {
      return formatMessage({ id: 'main_folder' })
    }

    return folderDetails?.name
  }

  const oldFolderName = getOldFolderName()

  return (
    <MoveItemsModal
      handleClose={handleOnClose}
      handleMove={handleMoveItems}
      isLoading={isLoading}
      onMoveLoading={moveItemsLoading}
      onMoveError={moveItemsError}
      destinationFolderHint={destinationFolderHint}
      modalTitle={modalTitle}
      entityName={projectDetails?.name}
      oldFolderName={oldFolderName}
    />
  )
}

export default MoveProjectModal
